import React, { useState } from "react";
import Icon from "@mdi/react";
import { mdiCloseCircleOutline, mdiClose } from "@mdi/js";

interface ItemSearchTagInputProps {
  value: string; // comma-separated tokens (kept stable for backend)
  onChange: (next: string) => void;
  placeholder?: string;
  id?: string;
}

const splitTokens = (s: string): string[] =>
  (s || "")
    .split(",")
    .map((t) => t.trim())
    .filter(Boolean);

const ItemSearchTagInput: React.FC<ItemSearchTagInputProps> = ({
  value,
  onChange,
  placeholder = "Add item code and press Enter",
  id,
}) => {
  const [draft, setDraft] = useState("");
  const tags = splitTokens(value);

  const commit = (raw: string) => {
    const parts = raw
      .split(",")
      .map((t) => t.trim())
      .filter(Boolean);
    if (!parts.length) return;
    const seen = new Set(tags.map((t) => t.toLowerCase()));
    const merged = [...tags];
    for (const p of parts) {
      if (!seen.has(p.toLowerCase())) {
        merged.push(p);
        seen.add(p.toLowerCase());
      }
    }
    onChange(merged.join(","));
    setDraft("");
  };

  const removeAt = (idx: number) => {
    const next = tags.filter((_, i) => i !== idx);
    onChange(next.join(","));
  };

  const onKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter" || e.key === ",") {
      e.preventDefault();
      commit(draft);
    } else if (e.key === "Backspace" && draft === "" && tags.length) {
      removeAt(tags.length - 1);
    }
  };

  const onPaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
    const pasted = e.clipboardData.getData("text");
    if (pasted.includes(",")) {
      e.preventDefault();
      commit(pasted);
    }
  };

  return (
    <div className="flex items-center gap-2">
      <div className="flex-1 min-h-10 rounded-md border bg-background px-2 py-1 focus-within:ring-2 focus-within:ring-ring flex flex-wrap items-center gap-1">
        {tags.map((t, idx) => (
          <span
            key={`${t}-${idx}`}
            className="inline-flex items-center gap-1 rounded-full bg-blue-100 text-blue-700 text-xs px-2 py-0.5"
          >
            {t}
            <button
              type="button"
              onClick={() => removeAt(idx)}
              className="hover:text-red-600"
              aria-label={`Remove ${t}`}
            >
              <Icon path={mdiClose} size={0.5} />
            </button>
          </span>
        ))}
        <input
          id={id}
          className="flex-1 min-w-[120px] bg-transparent text-sm outline-none py-1"
          placeholder={tags.length === 0 ? placeholder : ""}
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={onKeyDown}
          onPaste={onPaste}
          onBlur={() => {
            if (draft.trim()) commit(draft);
          }}
        />
      </div>
      {tags.length > 0 && (
        <button
          onClick={() => {
            onChange("");
            setDraft("");
          }}
          className="text-gray-400 hover:text-red-500 transition-colors"
          title="Clear all item codes"
        >
          <Icon path={mdiCloseCircleOutline} size={0.7} />
        </button>
      )}
    </div>
  );
};

export default ItemSearchTagInput;
